import { createTheme } from "@mui/material/styles";
import { color, font, type, motion, space } from "./tokens";

// MUI theme built from design/tokens.js. Kept deliberately thin: this design
// leans on semantic elements + `sx` rather than MUI's card/paper/elevation
// language, so the theme's job is mostly to stop MUI's defaults (Roboto,
// rounded buttons, uppercase labels, drop shadows) from leaking through.

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: color.ink, dark: "#000000", light: color.inkSoft, contrastText: color.paper },
    secondary: { main: color.clay, dark: color.clayDeep, light: color.clayLight, contrastText: color.paper },
    background: { default: color.paper, paper: color.paper },
    text: { primary: color.ink, secondary: color.graphite, disabled: color.mute },
    divider: color.line,
    error: { main: color.danger },
    success: { main: color.success },
  },

  shape: { borderRadius: 0 },

  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1440 },
  },

  typography: {
    fontFamily: font.body,
    htmlFontSize: 16,
    h1: type.displayXl,
    h2: type.displayLg,
    h3: type.displayMd,
    h4: type.displaySm,
    h5: { ...type.body, fontWeight: 700, fontSize: "1.125rem", lineHeight: 1.4 },
    h6: { ...type.body, fontWeight: 700, fontSize: "1rem", lineHeight: 1.45 },
    subtitle1: type.lead,
    subtitle2: type.label,
    body1: type.body,
    body2: type.bodySm,
    caption: type.caption,
    overline: type.label,
    button: {
      fontFamily: font.body,
      fontWeight: 700,
      fontSize: "0.8125rem",
      letterSpacing: "0.1em",
      textTransform: "uppercase",
    },
  },

  // No elevation anywhere. Depth in this design comes from ink/paper contrast
  // and hairlines, never from shadows.
  shadows: Array(25).fill("none"),

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: color.paper, color: color.ink },
      },
    },
    MuiContainer: {
      defaultProps: { maxWidth: false },
      styleOverrides: {
        root: {
          width: "100%",
          maxWidth: space.maxWidth,
          marginInline: "auto",
          paddingInline: space.gutter,
          "@media (min-width:600px)": { paddingInline: space.gutter },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: { disableRipple: true },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 0,
          minHeight: 52,
          paddingInline: 30,
          transition: `background-color ${motion.base} ${motion.ease}, color ${motion.base} ${motion.ease}, border-color ${motion.base} ${motion.ease}`,
        },
      },
    },
    MuiLink: {
      defaultProps: { underline: "none" },
      styleOverrides: { root: { color: "inherit" } },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: "none", borderRadius: 0 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 0 },
        notchedOutline: { borderColor: color.line },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 0, backgroundColor: color.paper } },
    },
    MuiDrawer: {
      styleOverrides: { paper: { borderRadius: 0, backgroundColor: color.ink, backgroundImage: "none" } },
    },
    MuiAccordion: {
      defaultProps: { disableGutters: true, square: true, elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          borderBottom: `1px solid ${color.line}`,
          "&::before": { display: "none" },
        },
      },
    },
  },
});

export default theme;
